import { useMemo, useState, type MouseEvent } from "react";

interface Props {
  values: number[]; // 0..1
  width?: number;
  height?: number;
}

/** Grafico big con crosshair + tooltip. Per il MarketDetailModal. */
export function BigChart({ values, width = 620, height = 220 }: Props) {
  const [hover, setHover] = useState<{ x: number; y: number; v: number } | null>(
    null,
  );

  const path = useMemo(() => {
    if (values.length < 2) return { line: "", area: "", color: "var(--yes)" };
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || 1;
    const step = width / (values.length - 1);
    const pts = values.map((v, i) => {
      const x = i * step;
      const y = height - ((v - min) / range) * (height - 20) - 10;
      return [x, y] as const;
    });
    const line = pts.map(([x, y], i) => `${i === 0 ? "M" : "L"}${x.toFixed(1)},${y.toFixed(1)}`).join(" ");
    const area = `${line} L${width},${height} L0,${height} Z`;
    const trend = values[values.length - 1] - values[0];
    const color = trend >= 0 ? "var(--yes)" : "var(--no)";
    return { line, area, color, pts };
  }, [values, width, height]);

  const onMove = (e: MouseEvent<SVGSVGElement>) => {
    const rect = (e.currentTarget as SVGSVGElement).getBoundingClientRect();
    const x = e.clientX - rect.left;
    if (!path.pts) return;
    const i = Math.round((x / rect.width) * (path.pts.length - 1));
    const pt = path.pts[Math.max(0, Math.min(path.pts.length - 1, i))];
    if (!pt) return;
    setHover({ x: pt[0], y: pt[1], v: values[i] ?? 0 });
  };

  return (
    <div className="relative">
      <svg
        width="100%"
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        onMouseMove={onMove}
        onMouseLeave={() => setHover(null)}
      >
        <defs>
          <linearGradient id="bc-grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={path.color} stopOpacity="0.32" />
            <stop offset="100%" stopColor={path.color} stopOpacity="0" />
          </linearGradient>
        </defs>
        {[0.25, 0.5, 0.75].map((p) => (
          <line
            key={p}
            x1="0"
            x2={width}
            y1={height * p}
            y2={height * p}
            stroke="var(--line)"
            strokeDasharray="3 4"
          />
        ))}
        <path d={path.area} fill="url(#bc-grad)" />
        <path
          d={path.line}
          fill="none"
          stroke={path.color}
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        />
        {hover && (
          <>
            <line
              x1={hover.x}
              x2={hover.x}
              y1="0"
              y2={height}
              stroke="var(--line-strong)"
              strokeDasharray="3 3"
            />
            <circle
              cx={hover.x}
              cy={hover.y}
              r={4}
              fill={path.color}
              stroke="var(--bg-0)"
              strokeWidth={2}
            />
          </>
        )}
      </svg>
      {hover && (
        <div
          className="pointer-events-none absolute -translate-x-1/2 -translate-y-full rounded-[8px] border border-line-strong bg-bg-2 px-2 py-1 text-[12px]"
          style={{ left: `${(hover.x / width) * 100}%`, top: hover.y - 4 }}
        >
          <span className="font-mono text-text">
            {(hover.v * 100).toFixed(1)}%
          </span>
        </div>
      )}
    </div>
  );
}
